
import React from 'react';
import { Modal } from 'antd';
import Lang from '../model/global/Lang';
import MessageBoxUtil from '../util/MessageBoxUtil';
import Moon from '../model/global/Moon';

const withAutoLogout = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  return class extends React.Component<P, { lastActivityTime: number }> {
    private inactivityTimer: number | null = null;
    private warningTimer: number | null = null;
    private activityHandler: (() => void) | null = null;
    private isMounted: boolean = false;

    constructor(props: P) {
      super(props);
      this.state = {
        lastActivityTime: Date.now()
      };
    }

    componentDidMount() {
      this.isMounted = true;
      if (Moon.getSingleton().user.hasLogin()) {
        this.setupActivityListeners();
        this.startInactivityTimer();
      }
    }

    componentDidUpdate(prevProps: P) {
      // 只在props变化时处理，避免频繁更新
      if (this.props !== prevProps) {
        const isLoggedIn = Moon.getSingleton().user.hasLogin();
        
        if (isLoggedIn && !this.inactivityTimer) {
          this.setState({ lastActivityTime: Date.now() });
          this.setupActivityListeners();
          this.startInactivityTimer();
        } else if (!isLoggedIn && this.inactivityTimer) {
          this.cleanup();
        }
      }
    }

    componentWillUnmount() {
      this.isMounted = false;
      this.cleanup();
    }

    setupActivityListeners = () => {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click'];
      
      this.activityHandler = () => {
        if (this.isMounted) {
          this.setState({ lastActivityTime: Date.now() });
          // 取消警告
          if (this.warningTimer) {
            clearTimeout(this.warningTimer);
            this.warningTimer = null;
          }
        }
      };
      
      events.forEach(event => {
        document.addEventListener(event, this.activityHandler!);
      });
    }

    removeActivityListeners = () => {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click'];
      
      if (this.activityHandler) {
        events.forEach(event => {
          document.removeEventListener(event, this.activityHandler!);
        });
        this.activityHandler = null;
      }
    }

    startInactivityTimer = () => {
      this.clearTimers();
      
      this.inactivityTimer = window.setInterval(() => {
        if (!this.isMounted) return;
        
        const currentTime = Date.now();
        const inactiveTime = currentTime - this.state.lastActivityTime;
        const logoutTimeout = 15 * 60 * 1000;
        const warningTimeout = 14 * 60 * 1000;
        
        if (inactiveTime > warningTimeout && inactiveTime < logoutTimeout && !this.warningTimer) {
          this.showWarning();
        }
        
        if (inactiveTime > logoutTimeout && Moon.getSingleton().user.hasLogin()) {
          this.handleAutoLogout();
        }
      }, 5000); // 改为5秒检查一次
    }

    showWarning = () => {
      if (this.warningTimer || !Moon.getSingleton().user.hasLogin()) return;
      
      this.warningTimer = window.setTimeout(() => {
        if (!this.isMounted || !Moon.getSingleton().user.hasLogin()) return;
        
        Modal.warning({
          title: Lang.t('sessionAboutToExpire') || '会话即将过期',
          content: Lang.t('sessionWillExpireSoon') || '由于长时间无操作，您的会话即将过期。请点击"保持登录"继续使用。',
          okText: Lang.t('stayLoggedIn') || '保持登录',
          onOk: () => {
            if (this.isMounted) {
              this.setState({ lastActivityTime: Date.now() });
            }
            this.warningTimer = null;
          },
          onCancel: () => {
            this.warningTimer = null;
          }
        });
      }, 1000);
    }

    handleAutoLogout = () => {
      if (!Moon.getSingleton().user.hasLogin()) return;
      
      this.cleanup();
      MessageBoxUtil.info(Lang.t('sessionExpired') || '由于长时间无操作，您已自动退出登录');
      
      Moon.getSingleton().user.httpLogout(() => {
        window.location.href = '/user/login';
      });
    }

    clearTimers = () => {
      if (this.inactivityTimer) {
        clearInterval(this.inactivityTimer);
        this.inactivityTimer = null;
      }
      
      if (this.warningTimer) {
        clearTimeout(this.warningTimer);
        this.warningTimer = null;
      }
    }

    cleanup = () => {
      this.clearTimers();
      this.removeActivityListeners();
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
};

export default withAutoLogout;